import { useCallback, useState } from 'react';
import { Pressable, RefreshControl, ScrollView, Text, View } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { ActivityIndicator } from 'react-native-paper';
import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'expo-router';
import Chip from '@/components/General/Chip';
import EmptyListPlaceholder from '@/components/General/EmptyListPlaceholder';
import Unicon from '@/components/General/Unicon';
import IconButton from '@/components/General/IconButton';
import SolidButton from '@/components/General/SolidButton';
import Avatar from '@/components/People/Avatar';
import SheetSelector from '@/components/General/inputs/SheetSelector';
import CutCard from '@/components/General/CutCard';
import FollowUpHeader from '@/components/FollowUp/FollowUpHeader';
import NeedsAttentionCard from '@/components/FollowUp/NeedsAttentionCard';
import LogTouchSheet from '@/components/FollowUp/LogTouchSheet';
import CreateFollowUpTaskSheet from '@/components/FollowUp/CreateFollowUpTaskSheet';
import StartPathSheet from '@/components/FollowUp/Paths/StartPathSheet';
import StartCareSheet from '@/components/FollowUp/Care/StartCareSheet';
import { Query } from '@/utils/react-query/Queries';
import { Mutation } from '@/utils/react-query/Mutations';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import useOrganizationEntitlements from '@/utils/hooks/useOrganizationEntitlements';
import { canViewModule, isFreeOrg } from '@/utils/organizationEntitlements';
import { extractFollowUpCode } from '@/data/followUp';
import { parseTimeFrame } from '@/utils/dater';
import {
    getTouchTypeLabel,
    getConnectionResultLabel,
    getTaskPurposeLabel,
} from '@/utils/constants/followUp';

/**
 * /follow-up — the Follow Up Overview (spec §6.1, default landing for the
 * module): Needs Attention, Needs Owner, Due & Overdue work, and Recent Team
 * Activity from follow_up_get_overview, plus fast care actions.
 *
 * Needs Attention renders ONE card per person — all their reasons (with
 * audience scope and snooze state) grouped together, never duplicate cards.
 * The evaluatedAt line is the honest freshness contract for derived signals
 * (§11.7). Team-gated; the server enforces 'View Follow Up' regardless.
 */

const NeedsOwnerRow = ({ item, organizationID }) => {
    const router = useRouter();
    const queryClient = useQueryClient();
    const [pickerOpen, setPickerOpen] = useState(false);

    const assignMutation = Mutation.followUp.setPrimaryCaregiver({
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QueryKeys.followUp.needsOwner(organizationID) });
        },
    });

    return (
        <View style={styles.needsRow} testID={`follow-up-needs-owner-${item.personID}`}>
            <Avatar size={36} profile={{ id: item.personID, firstName: item.firstName, lastName: item.lastName, avatarStorageKey: item.avatarStorageKey, avatarUpdatedAt: item.avatarUpdatedAt }} onPress={() => router.push(`/people/${item.personID}?tab=followup`)} />
            <Pressable
                style={styles.needsBody}
                onPress={() => router.push(`/people/${item.personID}?tab=followup`)}
                accessibilityRole="button"
            >
                <Text style={styles.needsName} numberOfLines={1}>{item.name}</Text>
                <View style={styles.rowBadges}>
                    <Chip icon="clipboard-outline" colorVariant="warning">
                        {`${item.openTaskCount} open task${item.openTaskCount === 1 ? '' : 's'}`}
                    </Chip>
                    {!!item.reason && <Chip icon="person-add-outline" colorVariant="textSecondary">Needs a primary connector</Chip>}
                </View>
            </Pressable>
            <SheetSelector
                type="people"
                title="Primary Connector"
                open={pickerOpen}
                onOpen={() => setPickerOpen(true)}
                onClose={() => setPickerOpen(false)}
                excludeIds={item.personID ? [item.personID] : []}
                onChange={(person) => {
                    if (!person?.id) return;
                    assignMutation.mutate({ organizationID, personID: item.personID, caregiverPersonID: person.id });
                }}
            >
                <IconButton
                    testID={`follow-up-needs-owner-assign-${item.personID}`}
                    iconName="person-add-outline"
                    iconSize={20}
                    buttonSize={36}
                    type="paths"
                    tooltipText="Assign connector"
                    onPress={() => setPickerOpen(true)}
                />
            </SheetSelector>
        </View>
    );
};

const DueWorkRow = ({ item }) => {
    const router = useRouter();
    const due = item.dueDate ? new Date(item.dueDate) : null;

    return (
        <Pressable
            testID={`due-work-${item.taskInstanceID}`}
            accessibilityRole="button"
            onPress={() => router.push(`/tasks/instances/${item.taskInstanceID}`)}
            style={styles.listRow}
        >
            <Avatar size={36} profile={{ id: item.personID, firstName: item.firstName, lastName: item.lastName, avatarStorageKey: item.avatarStorageKey, avatarUpdatedAt: item.avatarUpdatedAt }} onPress={() => router.push(`/tasks/instances/${item.taskInstanceID}`)} />
            <View style={styles.rowBody}>
                <Text style={styles.rowTitle} numberOfLines={1}>{item.title || getTaskPurposeLabel(item.purpose)}</Text>
                <Text style={styles.rowMeta} numberOfLines={1}>
                    {[
                        getTaskPurposeLabel(item.purpose),
                        item.personName,
                        due ? `Due ${due.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : 'No due date',
                    ].filter(Boolean).join(' · ')}
                </Text>
            </View>
            {!!item.overdue && <Chip icon="alarm-outline" colorVariant="error">Overdue</Chip>}
            <Unicon name="chevron-forward-outline" size={16} colorVariant="textTertiary" />
        </Pressable>
    );
};

const RecentTouchRow = ({ item, loggedInPersonID }) => {
    const router = useRouter();
    const connected = !!item.isConnected;

    return (
        <Pressable
            testID={`recent-touch-${item.id}`}
            accessibilityRole="button"
            onPress={() => router.push(`/people/${item.personID}?tab=followup`)}
            style={styles.listRow}
        >
            <Avatar size={36} profile={{ id: item.personID, firstName: item.firstName, lastName: item.lastName, avatarStorageKey: item.avatarStorageKey, avatarUpdatedAt: item.avatarUpdatedAt }} onPress={() => router.push(`/people/${item.personID}?tab=followup`)} />
            <View style={styles.rowBody}>
                <Text style={styles.rowTitle} numberOfLines={1}>
                    {`${item.actorName || 'Someone'}${item.actorPersonID === loggedInPersonID ? ' (you)' : ''} → ${item.personName || 'Unknown'}`}
                </Text>
                <Text style={styles.rowMeta} numberOfLines={1}>
                    {`${getTouchTypeLabel(item.type)} · ${getConnectionResultLabel(item.type, item.connectionResult)} · ${parseTimeFrame(item.occurredAt, true)}`}
                </Text>
            </View>
            <Chip
                icon={connected ? 'checkmark-circle-outline' : 'arrow-forward-circle-outline'}
                colorVariant={connected ? 'success' : 'textSecondary'}
            >
                {connected ? 'Connected' : 'Attempt'}
            </Chip>
        </Pressable>
    );
};

const SectionEmpty = ({ text }) => <Text style={styles.sectionEmpty}>{text}</Text>;

const WorkPanel = ({ title, icon, children, testID }) => (
    <CutCard accentColor="paths" style={styles.panel} testID={testID}>
        <View style={styles.panelHeader}>
            <Unicon name={icon} size={18} colorVariant="paths" />
            <Text style={styles.sectionTitle}>{title}</Text>
        </View>
        {children}
    </CutCard>
);

const QUICK_ACTIONS = [
    { key: 'log', label: 'Log Touch', title: 'Log a touch for', icon: 'add' },
    { key: 'task', label: 'Create Task', title: 'Create a Follow Up task for', icon: 'clipboard-outline' },
    { key: 'care', label: 'Start Care', title: 'Start care for', icon: 'heart-outline' },
    { key: 'path', label: 'Start Path', title: 'Start a Path for', icon: 'git-branch-outline' },
    { key: 'open', label: 'Find Person', title: 'Open Follow Up for', icon: 'search' },
];

/**
 * Active care episode card (§25.9 Overview): person, the short episode
 * context, last-activity and staffing state, plus a Needs Owner flag when
 * the person has no primary caregiver. Taps through to the episode route.
 */
const CareEpisodeCard = ({ item }) => {
    const router = useRouter();
    const staffing = [];
    if ((item.openMealSlotCount || 0) > 0) staffing.push(`${item.openMealSlotCount} open meal${item.openMealSlotCount === 1 ? '' : 's'}`);
    if ((item.openTaskCount || 0) > 0) staffing.push(`${item.openTaskCount} open task${item.openTaskCount === 1 ? '' : 's'}`);

    return (
        <Pressable
            testID={`care-episode-${item.careEpisodeID}`}
            accessibilityRole="button"
            onPress={() => router.push(`/follow-up/care/${item.careEpisodeID}`)}
            style={styles.listRow}
        >
            <Avatar size={36} profile={{ id: item.personID, firstName: item.firstName, lastName: item.lastName, avatarStorageKey: item.avatarStorageKey, avatarUpdatedAt: item.avatarUpdatedAt }} onPress={() => router.push(`/follow-up/care/${item.careEpisodeID}`)} />
            <View style={styles.rowBody}>
                <Text style={styles.rowTitle} numberOfLines={1}>
                    {item.personName || 'Unknown person'}
                </Text>
                <Text style={styles.rowMeta} numberOfLines={1}>
                    {[
                        item.context || 'Care',
                        item.lastTouchAt ? `Last activity ${parseTimeFrame(item.lastTouchAt, true)}` : 'No visits logged yet',
                    ].filter(Boolean).join(' · ')}
                </Text>
                {staffing.length > 0 && (
                    <Text style={styles.rowMeta} numberOfLines={1}>{staffing.join(' · ')}</Text>
                )}
            </View>
            {!item.primaryCaregiverPersonID && (
                <Chip icon="person-add-outline" colorVariant="warning">Needs Owner</Chip>
            )}
            <Unicon name="chevron-forward-outline" size={16} colorVariant="textTertiary" />
        </Pressable>
    );
};

export default function FollowUpOverviewScreen() {
    const router = useRouter();
    const queryClient = useQueryClient();
    const { data: loggedInUser } = Query.people.loggedInUser();
    const { entitlements } = useOrganizationEntitlements();

    const organizationID = loggedInUser?.organizationID;
    const canView = !!loggedInUser?.can?.('View Follow Up');
    const canManage = !!loggedInUser?.can?.('Manage Follow Up');
    const moduleAllowed = canViewModule(entitlements, 'paths');
    const gated = !!loggedInUser && canView && moduleAllowed;

    const { data: overview, isLoading, isError, error, isRefetching } = Query.followUp.overview({
        organizationID,
        options: { enabled: gated },
    });
    const { data: audiences } = Query.followUp.audiences({
        organizationID,
        options: { enabled: gated },
    });

    // Each action owns a visible picker anchor on web.
    const [pickerAction, setPickerAction] = useState(null); // 'log' | 'task' | 'path' | 'care' | 'open'
    const [logPerson, setLogPerson] = useState(null);
    const [taskPerson, setTaskPerson] = useState(null);
    const [pathPerson, setPathPerson] = useState(null);
    const [carePerson, setCarePerson] = useState(null);

    // Resolve the picked person's caregiver so Create Task can default the
    // assignee correctly (D07) instead of falling back to unassigned.
    const { data: taskPersonCare } = Query.followUp.person({
        organizationID,
        personID: taskPerson?.personID,
        options: { enabled: !!taskPerson?.personID },
    });

    const refresh = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: QueryKeys.followUp.overview(organizationID) });
        queryClient.invalidateQueries({ queryKey: ['followUp', organizationID, 'people'] });
        queryClient.invalidateQueries({ queryKey: ['followUp', organizationID, 'my'] });
        queryClient.invalidateQueries({ queryKey: QueryKeys.followUp.needsOwner(organizationID) });
        queryClient.invalidateQueries({ queryKey: QueryKeys.followUp.audiences(organizationID) });
    }, [queryClient, organizationID]);

    if (loggedInUser && (!canView || !moduleAllowed)) {
        return (
            <View style={styles.deniedWrap}>
                <EmptyListPlaceholder
                    type="404"
                    spacing={40}
                    message={!moduleAllowed && isFreeOrg(entitlements)
                        ? 'Follow Up is not available on this plan'
                        : "You don't have permission to view Follow Up. Contact your Reach administrator to be added to the Follow Up team."}
                />
            </View>
        );
    }

    if (isError) {
        const code = extractFollowUpCode(error) || error?.followUpCode;
        return (
            <View style={styles.deniedWrap}>
                <EmptyListPlaceholder
                    type="404"
                    spacing={40}
                    message={code === 'FOLLOW_UP_DENIED'
                        ? "You don't have permission to view Follow Up. Contact your Reach administrator to be added to the Follow Up team."
                        : 'Something went wrong loading the Follow Up overview. Pull to refresh to try again.'}
                />
            </View>
        );
    }

    const needsAttention = overview?.needsAttention || [];
    const needsOwner = overview?.needsOwner || [];
    const dueWork = overview?.dueWork || [];
    const recentTouches = overview?.recentTouches || [];
    const careEpisodes = overview?.activeCareEpisodes || [];

    // Per-audience policy: monitoring is off until at least one audience is
    // enabled. While the list is still loading the notice stays hidden.
    const setupNeeded = !!audiences && !audiences.some(audience => audience.isActive !== false);
    const selectPerson = (action, person) => {
        if (!person?.id || Array.isArray(person)) return;
        const selected = { personID: person.id, name: `${person.firstName || ''} ${person.lastName || ''}`.trim() };
        if (action === 'log') setLogPerson(selected);
        else if (action === 'task') setTaskPerson(selected);
        else if (action === 'path') setPathPerson(selected);
        else if (action === 'care') setCarePerson(selected);
        else router.push(`/people/${person.id}?tab=followup`);
        setPickerAction(null);
    };

    return (
        <View style={styles.container}>
            <ScrollView
                style={styles.scroll}
                contentContainerStyle={styles.content}
                keyboardShouldPersistTaps="handled"
                refreshControl={<RefreshControl refreshing={isRefetching} onRefresh={refresh} />}
            >
                <FollowUpHeader active="overview" canManage={canManage}>
                    <Text style={styles.freshness} testID="follow-up-evaluated-at">
                        {overview?.evaluatedAt
                            ? `Last evaluated ${parseTimeFrame(overview.evaluatedAt, true)}`
                            : 'Monitoring evaluation pending'}
                    </Text>
                </FollowUpHeader>

                <View style={styles.actionsRow}>
                    {QUICK_ACTIONS.map(action => (
                        <SheetSelector
                            key={action.key}
                            type="people"
                            title={action.title}
                            open={pickerAction === action.key}
                            onOpen={() => setPickerAction(action.key)}
                            onClose={() => setPickerAction(current => current === action.key ? null : current)}
                            onChange={person => selectPerson(action.key, person)}
                        >
                            <SolidButton
                                testID={`overview-${{ log: 'log-touch', task: 'create-task', care: 'start-care', path: 'start-path', open: 'find-person' }[action.key]}`}
                                size="sm"
                                variant={action.key === 'log' ? 'normal' : 'outlined'}
                                type="paths"
                                iconName={action.icon}
                                text={action.label}
                                onPress={() => setPickerAction(action.key)}
                            />
                        </SheetSelector>
                    ))}
                </View>

                {setupNeeded && (
                    <CutCard accentColor="paths" style={styles.setupCard} contentStyle={styles.setupContent} testID="follow-up-setup-notice">
                        <Unicon name="options-outline" size={22} colorVariant="paths" />
                        <View style={styles.rowBody}>
                            <Text style={styles.setupTitle}>Set up your monitoring audience</Text>
                            <Text style={styles.headerSubtitle}>
                                {'Needs Attention stays empty until at least one audience is enabled. You can still log touches, create tasks, and start paths.'}
                                {!canManage ? ' Ask a Follow Up manager to configure monitoring.' : ''}
                            </Text>
                        </View>
                        {canManage && <SolidButton testID="overview-configure-monitoring" size="sm" variant="outlined" type="paths" text="Configure" onPress={() => router.push('/follow-up/audiences')} />}
                    </CutCard>
                )}

                {isLoading ? (
                    <View style={styles.loadingWrap}><ActivityIndicator size="large" /></View>
                ) : (
                    <View style={styles.panelGrid}>
                        {careEpisodes.length > 0 && (
                            <WorkPanel title="Active Care" icon="heart-outline" testID="overview-care-panel">
                                {careEpisodes.map((item) => (
                                    <CareEpisodeCard key={item.careEpisodeID} item={item} />
                                ))}
                            </WorkPanel>
                        )}

                        <WorkPanel title="Needs Attention" icon="alert-circle-outline" testID="overview-attention-panel">
                        {needsAttention.length === 0 ? (
                            <SectionEmpty text={setupNeeded ? 'Enable an audience to see who needs attention.' : 'No one needs attention right now.'} />
                        ) : (
                            needsAttention.map((item) => (
                                <NeedsAttentionCard
                                    key={item.personID}
                                    personID={item.personID}
                                    name={item.name}
                                    profile={item}
                                    reasons={item.reasons}
                                    onPress={() => router.push(`/people/${item.personID}?tab=followup`)}
                                />
                            ))
                        )}
                        </WorkPanel>

                        {canManage && <WorkPanel title="Needs Owner" icon="person-add-outline" testID="overview-owner-panel">
                        {needsOwner.length === 0 ? (
                            <SectionEmpty text="Every open Follow Up task has an owner." />
                        ) : (
                            needsOwner.map((item) => (
                                <NeedsOwnerRow key={item.personID} item={item} organizationID={organizationID} />
                            ))
                        )}
                        </WorkPanel>}

                        <WorkPanel title="Due & Overdue" icon="time-outline" testID="overview-due-panel">
                        {dueWork.length === 0 ? (
                            <SectionEmpty text="No Follow Up work is due." />
                        ) : (
                            dueWork.map((item) => <DueWorkRow key={item.taskInstanceID} item={item} />)
                        )}
                        </WorkPanel>

                        <WorkPanel title="Recent Team Activity" icon="chatbubbles-outline" testID="overview-activity-panel">
                        {recentTouches.length === 0 ? (
                            <SectionEmpty text="No touches logged yet." />
                        ) : (
                            recentTouches.map((item) => (
                                <RecentTouchRow key={item.id} item={item} loggedInPersonID={loggedInUser?.id} />
                            ))
                        )}
                        </WorkPanel>
                    </View>
                )}
            </ScrollView>

            <LogTouchSheet
                isOpen={!!logPerson}
                onClose={() => setLogPerson(null)}
                organizationID={organizationID}
                personID={logPerson?.personID}
                personName={logPerson?.name}
            />
            <CreateFollowUpTaskSheet
                isOpen={!!taskPerson}
                onClose={() => setTaskPerson(null)}
                organizationID={organizationID}
                personID={taskPerson?.personID}
                personName={taskPerson?.name}
                primaryCaregiverPersonID={taskPersonCare?.primaryCaregiverPersonID}
            />
            <StartPathSheet
                isOpen={!!pathPerson}
                onClose={() => setPathPerson(null)}
                organizationID={organizationID}
                personID={pathPerson?.personID}
                personName={pathPerson?.name}
            />
            <StartCareSheet
                isOpen={!!carePerson}
                onClose={() => setCarePerson(null)}
                organizationID={organizationID}
                personID={carePerson?.personID}
                personName={carePerson?.name}
            />
        </View>
    );
}

const styles = StyleSheet.create(({ colors, fonts, shadows }) => ({
    container: { flex: 1 },
    scroll: { flex: 1 },
    content: { padding: 16, paddingBottom: 60, gap: 18, maxWidth: 1180, width: '100%', alignSelf: 'center' },
    deniedWrap: { flex: 1, justifyContent: 'center' },
    headerSubtitle: { fontSize: 13, color: colors.textSecondary, marginTop: 4, lineHeight: 19 },
    freshness: { fontSize: 11, color: colors.textTertiary },
    actionsRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 10 },
    setupCard: { padding: 18, backgroundColor: colors.pathsContainer, borderRadius: 3, boxShadow: shadows.card },
    setupContent: { flexDirection: 'row', alignItems: 'center', gap: 12, width: '100%' },
    setupTitle: { fontSize: 15, fontFamily: fonts.heading, color: colors.text },
    panelGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 18 },
    panel: { flexGrow: 1, flexShrink: 1, flexBasis: 440, maxWidth: '100%', minWidth: 0, padding: 16, backgroundColor: colors.surface, borderRadius: 3, boxShadow: shadows.card },
    panelHeader: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 16 },
    loadingWrap: { padding: 40, alignItems: 'center' },
    sectionTitle: {
        fontSize: 15,
        fontFamily: fonts.heading,
        color: colors.text,
    },
    sectionEmpty: {
        fontSize: 13,
        color: colors.textSecondary,
        paddingVertical: 16,
        lineHeight: 20,
    },
    needsRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        backgroundColor: colors.surface,
        borderRadius: 12,
        padding: 14,
        marginBottom: 8,
    },
    needsBody: { flex: 1, minWidth: 0, gap: 4 },
    needsName: { fontSize: 15, fontFamily: fonts.heading, color: colors.text },
    rowBadges: { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
    listRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        backgroundColor: colors.surface,
        borderRadius: 12,
        padding: 14,
        marginBottom: 8,
        _web: {
            cursor: 'pointer',
            transition: 'all 0.15s ease-in-out',
            _hover: { backgroundColor: colors.hover },
        },
    },
    iconWrap: {
        width: 32,
        height: 32,
        borderRadius: 8,
        backgroundColor: colors.pathsContainer,
        alignItems: 'center',
        justifyContent: 'center',
    },
    iconWrapConnected: { backgroundColor: colors.successContainer },
    iconWrapOverdue: { backgroundColor: colors.errorContainer },
    rowBody: { flex: 1, minWidth: 0 },
    rowTitle: { fontSize: 14, fontFamily: fonts.bold, color: colors.text },
    rowMeta: { fontSize: 12, color: colors.textSecondary, marginTop: 1 },
}));
